import { Trans } from '@lingui/react';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import {
  GqlCompany,
  GqlDocSystemTagKind,
  GqlMeeting,
  GqlMeetingKind,
  GqlShareIssueParticipant,
  GqlShareIssueRegistration,
  type GqlMeetingConcern,
  type GqlShareIssue,
} from '../../../../graphql/base-types.graphql';
import { DisplayDocument, Document } from '@startuptools/business-ui/document/Document';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { DocGenRelationName } from '@startuptools/common/doc-gen';
import { apiClient } from '../../../../common/http';
import { useApolloClient } from '@apollo/client';
import { ShareIssueDocument } from '../../ShareIssue.graphql';
import { Divider } from '@startuptools/ui/Divider';
import { BoardMembersTable } from '../../../common/components/BoardMembersTable';
import {
  useCreateShareIssueBoardMemberMutation,
  useDeleteShareIssueBoardMemberMutation,
} from './StepBoardStatement.graphql';
import { ParticipantsTable } from '../ParticipantsTable';
import { PaymentKind } from '@startuptools/common/participants';
import { Card } from '@startuptools/ui/Card';
import { DATE_FORMAT_LUXON, toDateTimeMaybe } from '@startuptools/common/common';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { RichTextInput } from '@startuptools/ui/RichTextInput';
import { useSiSetNonCashStatementMutation } from '../../../../graphql/react-operations';
import { useStepper } from '../../../../components/steppers/common/StepperContext';
import { ErrorDisplay } from '../../../../components/error-display/ErrorDisplay';
import { useStepperStepContent } from '../../../../components/steppers/common/StepperStepContentContext';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { captureException } from '@sentry/react';
import { genericErrorToast } from '@startuptools/ui/toast/use-toast';
import { DisplayIdentity } from '@startuptools/business-ui/identity/Identity';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';

const getDefaultFilename = (
  mc: Pick<GqlMeetingConcern, 'appendixNumber'> | null | undefined,
  shareIssue: Pick<GqlShareIssue, 'paymentKindOffset' | 'paymentKindNonCash'>,
) => {
  let appendixName = `${mc?.appendixNumber ?? 'x'}.3`;
  if (shareIssue.paymentKindOffset) {
    appendixName = `${mc?.appendixNumber ?? 'x'}.4`;
  }
  return `Appendix ${appendixName} - Revisorns yttrande angående apport.pdf`;
};

type FormValues = Pick<GqlShareIssue, 'nonCashStatementSl' | 'nonCashStatementPl'>;

export const SiStepPayByInKind = ({
  company,
  shareIssue,
  shareIssueRegistration,
}: {
  company: Pick<GqlCompany, 'id' | 'organizationNumber' | 'name'>;
  shareIssue: Pick<
    GqlShareIssue,
    | 'id'
    | 'companyId'
    | 'nonCashStatementPl'
    | 'nonCashStatementSl'
    | 'paymentKindOffset'
    | 'paymentKindNonCash'
    | 'paymentKindCash'
    | 'pricePerShare'
    | 'currency'
    | 'companyId'
    | 'maxNumberOfShares'
    | 'shareClasses'
    | 'secondaryLang'
  > & {
    meetingConcern?:
      | (Pick<GqlMeetingConcern, 'id' | 'appendixNumber'> & { meeting: Pick<GqlMeeting, 'id' | 'startTime' | 'kind'> })
      | null;
    boardMembers: { id: string; identity: DisplayIdentity }[];
    latestAuditorStatementNonCash?: DisplayDocument | null;
  };
  shareIssueRegistration: Pick<GqlShareIssueRegistration, 'id'> & {
    participants: (Pick<
      GqlShareIssueParticipant,
      'id' | 'paymentKind' | 'paymentStatus' | 'numberOfShares' | 'shareClass'
    > & {
      identity: DisplayIdentity;
      proxy?: DisplayIdentity | null;
    })[];
  };
}) => {
  const companyId = useCompanyId();
  const apolloClient = useApolloClient();
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      nonCashStatementPl: shareIssue.nonCashStatementPl || exampleText.Pl,
      nonCashStatementSl: shareIssue.nonCashStatementSl || exampleText.Sl,
    },
  });

  const [doSubmit, { loading }] = useSiSetNonCashStatementMutation({
    refetchQueries: [ShareIssueDocument],
    awaitRefetchQueries: true,
  });

  // eslint-disable-next-line sut/loading-mutation
  const [doDeleteBm] = useDeleteShareIssueBoardMemberMutation({
    refetchQueries: [ShareIssueDocument],
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doCreateBm] = useCreateShareIssueBoardMemberMutation({
    refetchQueries: [ShareIssueDocument],
  });

  const defaultFilename = getDefaultFilename(shareIssue.meetingConcern, shareIssue);

  const sips = shareIssueRegistration.participants.filter(sip => sip.paymentKind === PaymentKind.NonCash);

  const onSubmit: SubmitHandler<FormValues> = async values => {
    try {
      await doSubmit({
        variables: { companyId: shareIssue.companyId, input: { id: shareIssue.id, ...values } },
      });
      return stepNext();
    } catch (e) {
      captureException(e);
      genericErrorToast();
    }
  };

  return (
    <Stack gap={3}>
      <form id="inKindForm" onSubmit={handleSubmit(onSubmit)} />
      <ParticipantsTable
        paymentKind={PaymentKind.NonCash}
        shareIssue={shareIssue}
        sips={sips}
        sirId={shareIssueRegistration.id}
      />

      <Divider />

      <Card>
        <p>
          Styrelsen i {company.name}, org.nr {company.organizationNumber} (”Bolaget”), avger per{' '}
          {toDateTimeMaybe(shareIssue.meetingConcern?.meeting?.startTime)?.toFormat(DATE_FORMAT_LUXON)} härmed följande
          redogörelse enligt 13 kap. 7 § aktiebolagslagen med anledning av nyemissioner av aktier med betalning genom
          apportegendom som
          {shareIssue.meetingConcern?.meeting?.kind === GqlMeetingKind.BoardMeeting ? 'styrelsen' : 'bolagsstämman'} har
          för avsikt att fatta beslut om.
        </p>
        <Controller
          control={control}
          name="nonCashStatementPl"
          render={({ field }) => (
            <FormControl>
              <FormLabel>
                <Trans id="Board statement (Swedish):" />
              </FormLabel>
              <RichTextInput {...field} />
            </FormControl>
          )}
        />

        {shareIssue.secondaryLang && (
          <>
            <Divider />
            <p>
              The board in {company.name}, org.nr {company.organizationNumber} ("The Company"), hereby gives its
              statement regarding important events according to the Swedish Company Act chapter 14, paragraph 9
              concerning the issue of warrants by non-cash payment which{' '}
              {shareIssue.meetingConcern?.meeting?.kind === GqlMeetingKind.BoardMeeting
                ? 'the board'
                : 'the shareholders meeting'}{' '}
              has the intention of enacting.
            </p>
            <Controller
              control={control}
              name="nonCashStatementSl"
              render={({ field }) => (
                <FormControl>
                  <FormLabel>
                    <Trans id="Board statement (English):" />
                  </FormLabel>
                  <RichTextInput {...field} />
                </FormControl>
              )}
            />
          </>
        )}
      </Card>

      <Divider />

      <Typography level="h4">
        <Trans id="Who is signing?" />
      </Typography>

      <BoardMembersTable
        boardMembers={shareIssue.boardMembers}
        refetchQueries={[ShareIssueDocument]}
        onDelete={id => doDeleteBm({ variables: { companyId: shareIssue.companyId, id } })}
        onCreate={identity =>
          doCreateBm({
            variables: { companyId, input: { shareIssueId: shareIssue.id, identityInput: identityToInput(identity) } },
          })
        }
      />

      <Divider />

      <Typography level="h4">
        <Trans id="Upload auditor statement for in-kind payments" />
      </Typography>

      <Document
        defaultFileName={defaultFilename}
        document={shareIssue.latestAuditorStatementNonCash}
        upload={async data => {
          const file = data.file;
          const relation = {
            relation_id: shareIssue.id,
            relation_name: DocGenRelationName.ShareIssueId,
          };
          await apiClient.uploadFile(
            file,
            file.name,
            companyId,
            relation,
            false,
            GqlDocSystemTagKind.AuditorsStatementNonCash,
          );
          return apolloClient.refetchQueries({
            include: [ShareIssueDocument],
          });
        }}
      />

      {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

      <Box>
        <Button type="submit" form="inKindForm" loading={loading}>
          <Trans id="Save and Continue" />
        </Button>
      </Box>
    </Stack>
  );
};

const exampleText = {
  Pl: `
      <p>
      <strong>
        <u>Exempeltext:</u>
      </strong>
    </p>
    <p>1 250 aktier i Exempelbolaget AB, org.nr 559000-0000, till ett värdet av 1 000 kronor per aktie, till ett värde av totalt 1 250 000 kr, fördelat enligt följande:</p>
    <span>
      <p>Investerare&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Nummer&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Antal aktier</p>
      <p>ABC Invest AB&nbsp;&nbsp;&nbsp;&nbsp;556000-0000&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;500</p>
      <p>DEF Invest AB&nbsp;&nbsp;&nbsp;&nbsp;559000-0000&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;750</p>
    </span>
    <br>
    <b><i>Fyll i omständigheter som kan vara viktiga vid bedömning av värdet på apportegendomen, hur värdet på apportegendomen har bestämts och utifrån vilka rättsliga och ekonomiska synpunkter värderingen har gjorts.</i></b>
    <p>På grundval av den kännedom styrelsen har om den egendom som ska förvärvas är det styrelsens bedömning att värdet på apportegendomen som kan komma att tillföras Bolaget såsom apportegendom minst motsvarar värdet av det vederlag som ska utges, och beräknas komma att tas upp till detta värde i Bolagets balansräkning.
Styrelsen bedömer vidaere att apportegendomen kan antas bli till nytta för Bolagets verksamhet samt att värdet på apportegendomen inte har tagits upp till högre värde än det verkliga värdet för Bolaget</p>
    <p>Handlingar enligt 2 kap 9 § aktiebolagslagen (apportavtal) hålls tillgängliga på Bolagets adress.</p>

    `,
  Sl: `
    <p>
    <strong>
      <u>Sample text:</u>
    </strong>
    </p>
  <p>1 250 shares in Exempelbolaget AB, org.nr 559000-0000, at a value of 1 000 kr per share, to a value of total 1 250 000 kr, distributed as follows:</p>
    <span>
    <p>Investor&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Number&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Number of shares</p>
    <p>ABC Invest AB&nbsp;&nbsp;&nbsp;&nbsp;556000-0000&nbsp;&nbsp;&nbsp;&nbsp;500</p>
    <p>DEF Invest AB&nbsp;&nbsp;&nbsp;&nbsp;559000-0000&nbsp;&nbsp;&nbsp;&nbsp;750</p>
    </span>
    <br>
    <b><i>Fill in circumstances that may be important in assessing the value of the contribution property, how the value of the contribution property has been determined and based on which legal and economic views the valuation has been made.</i></b>
    <p>On the basis of the knowledge the board has about the property to be acquired, it is the board's assessment that the value of the contribution property that may be acquired by the Company as contribution property is at least equal to the value of the consideration to be paid, and is estimated to be taken up to this value in the Company's balance sheet.</p>
    <p>The board considers that the contribution property can be assumed to be useful to the Company's operations and that the value of the contribution property has not been taken up to a higher value than the actual value of the Company.</p>
    <p>Acts under section 2, paragraph 9, of the Company Act (contribution agreement) are available at the Company's address.</p>
    `,
};
